import React, { useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, Modal } from "@mui/material";
import { styled } from "@mui/system";
import { Disclosure, SupportedLanguage } from "./disclosure.types";
import { getDisclosureContent } from "./disclosures.config";
import { AcceptButton } from "./AcceptButton";
import { LanguageSelector } from "./LanguageSelector";

interface TransactionDisclosureModalProps {
  open: boolean;
  /** Called when modal closes. Reason is 'backdropClick' | 'escapeKeyDown' when user dismisses; undefined when user accepted. */
  onClose: (event?: any, reason?: string) => void;
  disclosure: Disclosure | null;
  selectedLanguage: SupportedLanguage;
  onLanguageChange: (language: SupportedLanguage) => void;
  onAccept: (disclosureId: string) => void;
  /** Optional override for the Note text (e.g. deposit-specific wording). String or a per-language map (falls back to `.en`). */
  noticeTextOverride?: string | Partial<Record<SupportedLanguage, string>>;
  /** When true, blurs the background behind the modal (e.g. for deposit disclosure so details aren't visible). */
  blurBackdrop?: boolean;
}

const ModalContainer = styled(Box)(() => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  outline: "none",
  overflow: "hidden",
  padding: "40px",
  alignItems: "center",
  gap: "24px",
  borderRadius: "28px",
  boxShadow: "0 4px 20px 0 rgba(173, 192, 203, 0.30)",
  // Default (mobile)
  width: "95%",
  maxWidth: "518px",
  maxHeight: "90vh",
  // Tablet (768px+)
  "@media (min-width: 768px)": {
    width: "90%",
    maxHeight: "85vh",
  },
  // Desktop (1024px+)
  "@media (min-width: 1024px)": {
    width: "518px",
    maxHeight: "90vh",
  },
}));

const Header = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  padding: 0,
  gap: "16px",
}));

const LanguageSelectorWrapper = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
});

const HeaderTitle = styled(Typography)(() => ({
  fontFamily: "Roobert",
  color: "#002035",
  textAlign: "center",
  width: "100%",
  fontSize: "24px",
  fontWeight: 500,
  lineHeight: "28px",
  letterSpacing: "-0.48px",
  fontStyle: "normal",
}));

const NoticeBox = styled(Box)({
  display: "flex",
  padding: "12px",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: "8px",
  alignSelf: "stretch",
  borderRadius: "10px",
  background: "#F8F9F9",
  color: "#002035",
});

const NoticeTitle = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "16px",
  color: "#002035",
});

const NoticeText = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
});

const ContentArea = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  width: "100%",
  alignSelf: "stretch",
  padding: 0,
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "4px",
    background: "rgba(200, 200, 200, 0.3)",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "4px",
    background: "rgba(150, 150, 150, 0.5)",
    "&:hover": {
      background: "rgba(150, 150, 150, 0.7)",
    },
  },
}));

const ContentText = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "16px",
  color: "#002035",
  whiteSpace: "pre-wrap",
});

const BulletPoint = styled(Typography)({
  fontFamily: "Roobert",
  fontSize: "14px",
  lineHeight: "1.7",
  color: "#002035",
  paddingLeft: "16px",
  position: "relative",
  marginBottom: "4px",
  "&::before": {
    content: '"•"',
    position: "absolute",
    left: 0,
  },
});

export const TransactionDisclosureModal: React.FC<TransactionDisclosureModalProps> = ({
  open,
  noticeTextOverride,
  onClose,
  disclosure,
  selectedLanguage,
  onLanguageChange,
  onAccept,
  blurBackdrop = false,
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Check if content is scrollable and if user has scrolled to bottom
  const checkScrollPosition = useCallback(() => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Consider scrolled to bottom if within 50px of the bottom
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;

      // Also check if content doesn't need scrolling (content fits in view)
      const contentFitsInView = scrollHeight <= clientHeight;

      if (isAtBottom || contentFitsInView) {
        setHasScrolledToBottom(true);
      }
    }
  }, []);

  // Reset scroll state when disclosure changes or modal opens
  useEffect(() => {
    if (open && disclosure) {
      setHasScrolledToBottom(false);
      // Scroll content area to top
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
      // Check after a short delay to allow content to render
      const timer = setTimeout(() => {
        checkScrollPosition();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, disclosure, selectedLanguage, checkScrollPosition]);

  if (!disclosure) return null;

  const content = getDisclosureContent(disclosure, selectedLanguage);
  if (!content) return null;

  // Language selector when the document declares language support (matches native)
  const showLanguageSelector =
    content.supportsLanguageSelection === true || (content.supportedLanguages?.length ?? 0) > 1;
  const supportedLanguages = content.supportedLanguages || ["en", "es", "zh", "vi", "so", "hmn"];

  // Determine if button should be disabled
  const isButtonDisabled = !hasScrolledToBottom;

  // Get button text
  const getButtonText = () => {
    if (isButtonDisabled) {
      return "Scroll to accept";
    }
    return content.acceptButtonText;
  };

  const handleAccept = () => {
    if (isButtonDisabled) return;
    onAccept(disclosure.id);
    onClose();
  };

  const handleScroll = () => {
    if (!hasScrolledToBottom) {
      checkScrollPosition();
    }
  };

  // Parse and render content with bullet points
  const renderContent = () => {
    const lines = content.content.split("\n");
    const elements: React.ReactNode[] = [];

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        // Empty line - add spacing
        elements.push(<Box key={`spacer-${index}`} sx={{ height: "8px" }} />);
        return;
      }

      // Check if it's a bullet point (starts with - or •)
      if (trimmedLine.startsWith("- ") || trimmedLine.startsWith("• ")) {
        const bulletText = trimmedLine.replace(/^[-•]\s*/, "");
        elements.push(<BulletPoint key={index}>{bulletText}</BulletPoint>);
      } else {
        // Regular paragraph
        elements.push(<ContentText key={index}>{trimmedLine}</ContentText>);
      }
    });

    return <Box>{elements}</Box>;
  };

  return (
    <Modal
      open={open}
      onClose={(e, reason) => onClose(e, reason)}
      slotProps={{
        ...(blurBackdrop && {
          backdrop: {
            sx: {
              backdropFilter: "blur(4px)",
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            },
          },
        }),
      }}>
      <ModalContainer>
        {/* Language Selector (above header) */}
        {showLanguageSelector && (
          <LanguageSelectorWrapper>
            <LanguageSelector
              selectedLanguage={selectedLanguage}
              onLanguageChange={onLanguageChange}
              supportedLanguages={supportedLanguages}
            />
          </LanguageSelectorWrapper>
        )}

        {/* Header with Title */}
        <Header>
          <HeaderTitle>{content.title}</HeaderTitle>
        </Header>

        {/* Notice Box */}
        <NoticeBox>
          <NoticeTitle>Note:</NoticeTitle>
          <NoticeText>
            {noticeTextOverride == null
              ? content.noticeText
              : typeof noticeTextOverride === "string"
                ? noticeTextOverride
                : noticeTextOverride[selectedLanguage] ?? noticeTextOverride.en}
          </NoticeText>
        </NoticeBox>

        {/* Content Area */}
        <ContentArea ref={contentRef} onScroll={handleScroll}>
          {renderContent()}
        </ContentArea>

        {/* Accept Button */}
        <AcceptButton onClick={handleAccept} disabled={isButtonDisabled} fullWidth>
          {getButtonText()}
        </AcceptButton>
      </ModalContainer>
    </Modal>
  );
};

export default TransactionDisclosureModal;
